import { Store } from "vuex";
import { ApiStatus } from "@/store/general.types";
import { DeleteImportedCitationAndFilePayload, DownloadCorruptionReportPayload, GetBulkCitationUploadSignedUrlsPayload, GetCitationUploadSignedUrlPayload, GetFailedImportsPayload, GetImportedCitationFilesPayload, GetProcessingCitationFilePayload, StudyState, UntrackFilesStatusPayload, UntrackFilesByStatusPayload, SaveStudyNotePayload, StudyNote, StudyNotesDTO, StudyExportPayload, StudyEnterRecordPayload } from "./study.types";
import { studyApi } from "@/api/study.api";

export const studyActions = {
    async getCitationUploadSignedUrl(store: Store<StudyState>, payload: GetCitationUploadSignedUrlPayload) {
        try {
            store.commit('GET_CITATION_UPLOAD_SIGNED_URL', ApiStatus.LOADING);

            const { data } = await studyApi.getCitationUploadSignedUrl(payload);
            store.commit('GET_CITATION_UPLOAD_SIGNED_URL', { data });
        } catch (error) {
            store.commit('GET_CITATION_UPLOAD_SIGNED_URL', { error });
        }
    },
    async getBulkCitationUploadSignedUrls(store: Store<StudyState>, payload: GetBulkCitationUploadSignedUrlsPayload) {
        try {
            store.commit('GET_BULK_CITATION_UPLOAD_SIGNED_URLS', ApiStatus.LOADING);

            const { data } = await studyApi.getBulkCitationUploadSignedUrls(payload);
            store.commit('GET_BULK_CITATION_UPLOAD_SIGNED_URLS', { data });
        } catch (error) {
            store.commit('GET_BULK_CITATION_UPLOAD_SIGNED_URLS', { error });
        }
    },
    async getProcessingCitationFiles(store: Store<StudyState>, payload: GetProcessingCitationFilePayload) {
        try {
            store.commit('GET_PROCESSING_CITATION_FILES', ApiStatus.LOADING);

            const { data } = await studyApi.getProcessingCitationFiles(payload);
            store.commit('GET_PROCESSING_CITATION_FILES', { data });
        } catch (error) {
            store.commit('GET_PROCESSING_CITATION_FILES', { error });
        }
    },
    async getProcessingFilesCounts(store: Store<StudyState>, projectId: number) {
        try {
            store.commit('GET_PROCESSING_FILES_COUNTS', ApiStatus.LOADING);

            const { data } = await studyApi.getProcessingFilesCounts(projectId);
            store.commit('GET_PROCESSING_FILES_COUNTS', { data });
        } catch (error) {
            store.commit('GET_PROCESSING_FILES_COUNTS', { error });
        }
    },
    async getUserProcessingFileStatusCounts(store: Store<StudyState>, projectId: number) {
        try {
            store.commit('GET_USER_PROCESSING_FILE_STATUS_COUNTS', ApiStatus.LOADING);

            const { data } = await studyApi.getUserProcessingFileStatusCounts(projectId);
            store.commit('GET_USER_PROCESSING_FILE_STATUS_COUNTS', { data });
        } catch (error) {
            store.commit('GET_USER_PROCESSING_FILE_STATUS_COUNTS', { error });
        }
    },
    async untrackFilesStatus(store: Store<StudyState>, { projectId, fileIds }: UntrackFilesStatusPayload) {
        try {
            store.commit('UNTRACK_FILES_STATUS', ApiStatus.LOADING);

            const { data } = await studyApi.untrackFileStatus({ projectId, fileIds });
            store.commit('UNTRACK_FILES_STATUS', { data });
        } catch (error) {
            store.commit('UNTRACK_FILES_STATUS', { error });
        }
    },
    async untrackFilesByStatus(store: Store<StudyState>, { projectId, status }: UntrackFilesByStatusPayload) {
        try {
            store.commit('UNTRACK_FILES_BY_STATUS', ApiStatus.LOADING);

            const { data } = await studyApi.untrackFilesByStatus({ projectId, status });
            store.commit('UNTRACK_FILES_BY_STATUS', { data });
        } catch (error) {
            store.commit('UNTRACK_FILES_BY_STATUS', { error });
        }
    },
    async getFailedImports(store: Store<StudyState>, payload: GetFailedImportsPayload) {
        try {
            store.commit('GET_FAILED_IMPORTS', ApiStatus.LOADING);

            const { data } = await studyApi.getFailedImports(payload);
            store.commit('GET_FAILED_IMPORTS', { data });
        } catch (error) {
            store.commit('GET_FAILED_IMPORTS', { error });
        }
    },
    async getImportedCitationFiles(store: Store<StudyState>, payload: GetImportedCitationFilesPayload) {
        try {
            store.commit('GET_IMPORTED_CITATION_FILES', ApiStatus.LOADING);

            const { data } = await studyApi.getImportedCitationFiles(payload);
            store.commit('GET_IMPORTED_CITATION_FILES', { data });
        } catch (error) {
            store.commit('GET_IMPORTED_CITATION_FILES', { error });
        }
    },
    async deleteImportedCitationAndFile(store: Store<StudyState>, payload: DeleteImportedCitationAndFilePayload) {
        try {
            store.commit('DELETE_IMPORTED_CITATION_AND_FILE', ApiStatus.LOADING);

            const { data } = await studyApi.deleteImportedCitationAndFile(payload);
            store.commit('DELETE_IMPORTED_CITATION_AND_FILE', { data });
        } catch (error) {
            store.commit('DELETE_IMPORTED_CITATION_AND_FILE', { error });
        }
    },
    async downloadCorruptionReport(store: Store<StudyState>, payload: DownloadCorruptionReportPayload) {
        try {
            store.commit('DOWNLOAD_CORRUPTION_REPORT', ApiStatus.LOADING);

            const { data } = await studyApi.downloadCorruptionReport(payload);
            store.commit('DOWNLOAD_CORRUPTION_REPORT', { data });
        } catch (error) {
            store.commit('DOWNLOAD_CORRUPTION_REPORT', { error });
        }
    },
    async getUserLatestFailedImport(store: Store<StudyState>, projectId: number) {
        try {
            store.commit('GET_USER_LATEST_FAILED_IMPORT', ApiStatus.LOADING);

            const { data } = await studyApi.getUserLatestFailedImport(projectId);
            store.commit('GET_USER_LATEST_FAILED_IMPORT', { data });
        } catch (error) {
            store.commit('GET_USER_LATEST_FAILED_IMPORT', { error });
        }
    },
    async createStudyNote(store: Store<StudyState>, payload: SaveStudyNotePayload) {
        try {
            store.commit('CREATE_STUDY_NOTE', ApiStatus.LOADING);

            const { data } = await studyApi.createStudyNote(payload);
            store.commit('CREATE_STUDY_NOTE', { data });
        } catch (error) {
            store.commit('CREATE_STUDY_NOTE', { error });
        }
    },
    async updateStudyNote(store: Store<StudyState>, payload: SaveStudyNotePayload) {
        try {
            store.commit('UPDATE_STUDY_NOTE', ApiStatus.LOADING);

            const { data } = await studyApi.updateStudyNote(payload);
            store.commit('UPDATE_STUDY_NOTE', { data });
        } catch (error) {
            store.commit('UPDATE_STUDY_NOTE', { error });
        }
    },
    async getStudyNotes(store: Store<StudyNote>, studyNotesPayLoad: StudyNotesDTO) {
        try {
            store.commit('GET_STUDY_NOTES', ApiStatus.LOADING);
            const { projectId, studyId } = studyNotesPayLoad;
            const { data } = await studyApi.getStudyNotes(projectId, studyId);
            store.commit('GET_STUDY_NOTES', { data });
        } catch (error) {
            store.commit('GET_STUDY_NOTES', { error })
        }
    },
    async exportCitations(store: Store<StudyState>, studyExportPayload: StudyExportPayload) {
        try {
            store.commit('EXPORT_CITATIONS', ApiStatus.LOADING);
            const { data } = await studyApi.exportCitations(studyExportPayload);
            store.commit('EXPORT_CITATIONS', { data });
        } catch (error: any) {
            store.commit('EXPORT_CITATIONS', { error })
        }
    },
    async getAuthorsByProject(store: Store<StudyState>, projectId: number) {
        try {
            store.commit('GET_AUTHORS_BY_PROJECT', ApiStatus.LOADING);
            const { data } = await studyApi.getAuthorsByProject(projectId);
            store.commit('GET_AUTHORS_BY_PROJECT', { data });
        } catch (error) {
            store.commit('GET_AUTHORS_BY_PROJECT', { error })
        }
    },
    async getPublicationTypes(store: Store<StudyState>, projectId: number) {
        try {
            store.commit('GET_PUBLICATION_TYPES', ApiStatus.LOADING);
            const { data } = await studyApi.getPublicationTypes(projectId);
            store.commit('GET_PUBLICATION_TYPES', { data });
        } catch (error) {
            store.commit('GET_PUBLICATION_TYPES', { error })
        }
    },
    async enterRecord(store: Store<StudyState>, payload: StudyEnterRecordPayload) {
        try {
            store.commit('ENTER_RECORD', ApiStatus.LOADING);
            const { data } = await studyApi.enterRecord(payload);
            store.commit('ENTER_RECORD', { data });
        } catch (error) {
            store.commit('ENTER_RECORD', { error })
        }
    },
}